/**
 * The member portal's floorplan payload (`/tenant-portal/floorplans/{id}/`)
 * and its mapping onto `@nodus/floorplan`'s room shape. Shared by the
 * floorplan page and the guides' floorplan block.
 */
import type { FloorAlarm, FloorplanRoom } from "@nodus/floorplan";
import type { FloorplanAlarmRegionRow } from "@/lib/api/client";

export interface ApiRoom {
  id: number;
  name: string;
  description: string | null;
  floor_name: string;
  address_name: string;
  room_type: string | null;
  floorplan_polygon: number[][][];
  floorplan_label_pos: [number, number] | null;
  is_available: boolean | null;
  in_use?: boolean | null;
  is_bookable: boolean;
  seats: number | null;
  has_camera: boolean;
  has_tv: boolean;
  has_speaker: boolean;
  current_event: { title: string; start: string; end: string; organizer_tenant_name: string | null } | null;
  next_free_slot_start: string | null;
  next_free_slot_duration_minutes: number | null;
  booking_slots: any[] | null;
  navigation_hints: string;
  is_tenant_assignable: boolean;
  square_meters: number | null;
  occupancy_tenant_name: string | null;
  occupancy_status: "occupied" | "ending" | "upcoming" | null;
  is_new_neighbour: boolean;
  is_new_member: boolean;
  floorplan_display: { show_name?: boolean; show_status?: boolean; label?: string };
  furniture: any[];
  /** #416: main room this room is merged into; the viewer folds it. */
  merged_into_id?: number | null;
}

export interface FloorplanDetail {
  id: number;
  name: string;
  floor_name: string;
  address_name: string;
  svg_content: string;
  viewbox: string;
  scale: number;
  connection_zones: { id: string; polygon: number[][]; name: string; type: "elevator" | "stairs" | "entrance" | "corridor"; floorConnectionId?: number; targetFloorplanIds: number[] }[];
  /** Connections serving this plan's floors: where each stops (zone popup) and service state. */
  floor_connections?: {
    id: number;
    name: string;
    connection_type: string;
    is_broken?: boolean;
    broken_note?: string;
    stops?: { floorplan_id: number; floorplan_name: string; label: string; zone_id: string }[];
  }[];
  floors: {
    floor_id: number;
    floor_name: string;
    address_name: string;
    address_id?: number;
    region: number[][];
    /** The floor's alarm; null when none is linked. */
    alarm?: FloorAlarm | null;
  }[];
  rooms: ApiRoom[];
  furniture?: any[];
  /** Each drawn location (floor, address, room) with its own alarm links; never one per plan. */
  alarm_regions?: FloorplanAlarmRegionRow[];
}

export interface FloorplanListItem {
  id: number;
  name: string;
  floor_name: string;
  address_name: string;
  room_count?: number;
}

export function toFloorplanRoom(room: ApiRoom): FloorplanRoom {
  return {
    id: room.id,
    name: room.name,
    description: room.description,
    floorName: room.floor_name,
    addressName: room.address_name,
    roomType: room.room_type,
    polygon: room.floorplan_polygon,
    labelPos: room.floorplan_label_pos,
    mergedIntoId: room.merged_into_id ?? null,
    isAvailable: room.is_available ?? undefined,
    inUse: room.in_use ?? undefined,
    isBookable: room.is_bookable,
    squareMeters: room.square_meters,
    currentEvent: room.current_event ? { title: room.current_event.title, start: room.current_event.start, end: room.current_event.end } : undefined,
    nextFreeSlotStart: room.next_free_slot_start,
    nextFreeSlotDurationMinutes: room.next_free_slot_duration_minutes,
    isTenantAssignable: room.is_tenant_assignable,
    occupancies: room.occupancy_tenant_name ? [{ id: 0, tenantName: room.occupancy_tenant_name, startDate: "", endDate: null }] : [],
    occupancyStatus: room.occupancy_status,
    isNewNeighbour: room.is_new_neighbour,
    isNewMember: room.is_new_member,
    display: room.floorplan_display ? {
      showName: room.floorplan_display.show_name,
      showStatus: room.floorplan_display.show_status,
      label: room.floorplan_display.label,
    } : undefined,
    furniture: room.furniture || [],
  };
}
